import { countryNames } from './isoCountries';
import { isPlainObject } from './object';

/**
 * Joins non-falsy elements of an array with a specified separator.
 * Keeps 0 as a non-falsy element.
 *
 * @example ['foo', null, 'bar', undefined, 'baz', '', 0] => 'foo bar baz 0'
 *
 * @param array - The array to join.
 * @param joinSequence - The separator to use between elements. Defaults to a space.
 * @return The joined string.
 */
const joinElements = (
  array: Array<string | number | null | undefined> = [],
  joinSequence = ' ',
) =>
  Array.isArray(array)
    ? array
        .filter(
          (element) =>
            element !== null && element !== undefined && element !== '',
        )
        .join(joinSequence)
    : '';

/**
 * Renders an address object to a single string.
 * @param address - The address object to render.
 * @param options - The options to render the address.
 * @param options.omitCountry - Whether to omit the country from the address.
 * @param options.rawCountry - Keep the ISO country code instead of localising it.
 * @returns The string representation of the address.
 * @example addressToString({buildingNumber: '19', city: 'Berlin', country: 'DE', postCode: '12345', streetName: 'Musterstraße'})
 * => 'Musterstraße 19, 12345 Berlin, Deutschland'
 */
export const addressToString = (
  address:
    | {
        additionalInfo?: string | null;
        buildingNumber?: string | null;
        city?: string | null;
        country?: string | null;
        postCode?: string | null;
        streetName?: string | null;
      }
    | null
    | undefined,
  options?: {
    omitCountry?: boolean;
    rawCountry?: boolean;
  },
) => {
  if (typeof address === 'string') {
    return address;
  }

  if (!isPlainObject(address)) {
    return '';
  }

  const {
    additionalInfo,
    buildingNumber,
    city,
    country,
    postCode,
    streetName,
  } = address;

  const countryPart =
    options?.omitCountry === true || !country
      ? undefined
      : options?.rawCountry === true
        ? country
        : countryNames.getName(country, 'de');

  const addressString = joinElements(
    [
      joinElements([streetName, buildingNumber]),
      additionalInfo,
      joinElements([postCode, city]),
      countryPart,
    ],
    ', ',
  );

  return addressString;
};
